import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Code, Alert, Group, Button, Space, Text, Modal, createStyles, useMantineTheme, Tooltip} from '@mantine/core';
import { IconCircleCheck, IconExclamationCircle, IconX, IconCloudUpload, IconDownload } from '@tabler/icons-react';
import { Dropzone } from '@mantine/dropzone';

const useStyles = createStyles((theme) => ({
    dropzone: {
        position: 'relative',
        background: 'white !important',
        '&[data-accept]': {
            background: 'white',
        },

        '&[data-reject]': {
            background: 'white'
        },
    },

    dropzoneDisabled: {
        backgroundColor: theme.colors.gray[0],
        borderColor: theme.colors.gray[2],
        cursor: 'not-allowed',

        '& *': {
            color: theme.colors.gray[5],
        },
    },

    flexText: {
        display: 'flex',
        width: '100%',
        flexGrow: 1,
    },

    flexTextSpan: {
        fontWeight: 600,
        display: 'flex',
        flex: 1,
        textAlign: 'right',

        // Before
        '&::before': {
            content: '""',
            borderBottom: `1.5px dotted ${theme.colors.gray[5]}`,
            flexGrow: 1,
            display: 'inline-block',
            height: '1px',
            transform: 'translateY(1.05em)',
            margin: '0 0.4em',
        }
    },
}));

const downloadCSVExample = () => {
    const exampleData = `participant_id,survey_id,fingerprint_id,Q1,Q2,Q1-response,Q2-response
p001,survey123,1736978717555.uJA9AW,Tell us about yourself.,How do you choose which airline to fly?,I'm a software engineer with a passion for travel and photography,I choose airlines based on their safety record and in-flight comfort.
p002,survey123,1736978872680.3BHKKS,Tell us about yourself.,How do you choose which airline to fly?,big ones for everyone,I get the best deal and buy that shampoo right away.
p003,survey123,1736978940357.J9QBSq,Tell us about yourself.,How do you choose which airline to fly?,"As a student studying biology, I spend my free time hiking.",I prefer airlines with student discounts and flexible baggage policies.
p004,survey123,1736978960780.ZbwcJH,Tell us about yourself.,How do you choose which airline to fly?,"I work in marketing, and I enjoy cooking and yoga in my spare time.",Customer service and frequent flyer programs are important to me.
p005,survey123,1736978975223.IcUgWm,Tell us about yourself.,How do you choose which airline to fly?,my favorite type of chocolate is milk chocolate,I get the best deal and buy those shampoos right away.`;
    // Download to alias-example.csv
    const element = document.createElement('a');
    const file = new Blob([exampleData], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = 'alias-example.csv';
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    document.body.removeChild(element);
};

const calcCreditsToPurchase = (numRows, APICreditsAvailable) => {
    let creditsToPurchase = numRows - APICreditsAvailable;
    if (creditsToPurchase > 20000) creditsToPurchase = 20000;
    if (creditsToPurchase < 5) creditsToPurchase = 5;
    return creditsToPurchase;
}

const AliasFileUpload = ({ currentUser, APICreditsAvailable, setAPICreditsAvailable, uploadModalOpened, setUploadModalOpened, surveyIds, setSurveyIds, loadingSurveys, setLoadingSurveys }) => {

    const theme = useMantineTheme();
    const [wrongFileType, setWrongFileType] = useState(false);
    const [filename, setFilename] = useState('');
    const [fileOk, setFileOk] = useState(false);
    const [errorReadingFile, setErrorReadingFile] = useState(false);
    const [wrongFileFormat, setWrongFileFormat] = useState(false);
    const [fileFormatProblems, setFileFormatProblems] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [numRows, setNumRows] = useState(0);
    const [csvData, setCsvData] = useState([]);
    const [isStartingCall, setIsStartingCall] = useState(false);
    const [startedCall, setStartedCall] = useState(false);
    const [surveyId, setSurveyId] = useState('');

    const { cx, classes } = useStyles();

    const dropZoneOverlayStyes = {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: '0',
        left: '0',
        borderRadius: theme.radius.lg
    };

    const prepareForUpload = async (filenameArray) => {
        resetState();
        if (filenameArray.length === 0) return;
        const file = filenameArray[0];
        if (file.size > 5 * 1024 * 1024) { // 5 MB in bytes
            setWrongFileFormat(true);
            setFileFormatProblems(['File size exceeds 5MB']);
            return;
        }
        setUploading(true);
        setFilename(file.name);
        await parseData(file);
    };

    const resetState = () => {
        setWrongFileType(false);
        setFilename('');
        setFileOk(false);
        setErrorReadingFile(false);
        setWrongFileFormat(false);
        setFileFormatProblems([]);
        setUploading(false);
        setNumRows(0);
        setCsvData([]);
        setIsStartingCall(false);
        setStartedCall(false);
        setSurveyId('');
    };

    const parseData = async (file) => {
        if (!file) return;
    
        try {
            const reader = new FileReader();
    
            reader.onload = async (e) => {
                // Encode the file content in base64
                const base64Content = e.target.result.split(',')[1];
    
                // Convert the base64 encoded content to UTF-8 by calling your Netlify function
                const conversionResponse = await fetch("/.netlify/functions/convert-to-utf8", {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ fileContent: base64Content }),
                });
    
                const conversionResult = await conversionResponse.json();
                if (!conversionResult.success) {
                    setErrorReadingFile(true);
                    setUploading(false);
                    return;
                }

                const data = JSON.stringify({ csvString: conversionResult.utf8Text });
                const response = await fetch("/.netlify/functions/parseRawCSV", { 
                    method: "POST", 
                    headers: { 'Content-Type': 'application/json' },
                    body: data 
                });
                const { numRows, cleanedString, success, formatFails, surveyId } = await response.json();

                if (!success) {
                    setErrorReadingFile(true);
                    setUploading(false);
                    return;
                }
                if (surveyIds.includes(surveyId)) formatFails.push('Survey ID already exists');
                if (formatFails.length === 0) {
                    setFileOk(true);
                    setCsvData(cleanedString);
                    setNumRows(numRows);
                    setSurveyId(surveyId);
                } else {
                    setFileOk(false);
                    setWrongFileFormat(true);
                    setFileFormatProblems(formatFails);
                }
                setUploading(false);
            };
    
            reader.onerror = (e) => {
                console.log('Error reading file:', e);
                setErrorReadingFile(true);
            };
    
            // Read the file as a Data URL to get base64 encoded string
            reader.readAsDataURL(file);
        } catch (error) {
            console.log('Error uploading file:', error);
            setErrorReadingFile(true);
        }
    }    

    const runAliasAPI = async () => {
        setIsStartingCall(true);
        try {
            const data = JSON.stringify({ csvData, userId: currentUser.uid, numRows, filename });
            const response = await fetch("/.netlify/functions/upload-alias-to-firestore", { method: "POST", body: data });
            const { success } = await response.json();
            if (!success) throw new Error('Error uploading data to Firestore');
            setStartedCall(true);
            setLoadingSurveys([...loadingSurveys, surveyId]);
            setAPICreditsAvailable(APICreditsAvailable - numRows);
            // Update survey ids
            const sortedSurveys = [...surveyIds, surveyId].sort();
            setSurveyIds(sortedSurveys);
        } catch (error) {
            setErrorReadingFile(true);
        }
        setIsStartingCall(false);
    };

    return (
        <Modal
            opened={uploadModalOpened}
            radius='lg'
            title={'Upload CSV dataset and call Alias API'}
            centered
            padding='xl'
            size={500}
            className={classes.modal}
            onClose={() => {
                resetState();
                if (!uploading) setUploadModalOpened(false);
            }}
            withCloseButton={uploading === false}
        >
            {
                startedCall ?
                    <Text size='sm'>
                        Your API calls have been started. The results will appear under "Your Surveys" and <Code>{surveyId}</Code> and will be streamed until they complete (refresh to page to reload and check). If the survey doesn't finish within 24 hours, please contact us at support@roundtable.ai.
                    </Text>
                    :
                    <>
                        <Dropzone
                            onDrop={prepareForUpload}
                            onReject={() => {
                                setWrongFileType(true);
                            }}
                            multiple={false}
                            radius='lg'
                            accept={['text/csv']}
                            className={uploading === false ? classes.dropzone : cx(classes.dropzone, classes.dropzoneDisabled)}
                            loading={uploading}
                            disabled={uploading}
                        >
                            <Space h='md' />
                            <Dropzone.Reject>
                                <div style={{ ...dropZoneOverlayStyes, background: 'rgba(255,0,0,0.05)' }} />
                            </Dropzone.Reject>
                            <Dropzone.Accept>
                                <div style={{ ...dropZoneOverlayStyes, background: 'rgba(0,0,0,0.05)' }} />
                            </Dropzone.Accept>
                            <Group position="center">
                                <Dropzone.Accept>
                                    <IconCloudUpload
                                        size={40}
                                        stroke={1.5}
                                        color={theme.colors[theme.primaryColor][6]}
                                    />
                                </Dropzone.Accept>
                                <Dropzone.Reject>
                                    <IconX
                                        size={40}
                                        stroke={1.5}
                                        color={theme.colors.red[6]}
                                    />
                                </Dropzone.Reject>
                                <Dropzone.Idle>
                                    <IconCloudUpload
                                        size={40}
                                        stroke={1.5}
                                        color={uploading ? theme.colors.gray[5] : theme.colors[theme.primaryColor][6]}
                                    />
                                </Dropzone.Idle>
                            </Group>
                            <Space h='md' />
                            <Text size="lg" ta='center'>
                                Upload a CSV file or drop it here
                            </Text>
                            <Space h='md' />
                        </Dropzone>
                        <Space h='xl' />
                        {
                            wrongFileType ?
                                <>
                                    <Alert
                                        color='red'
                                        radius='md'
                                        title='Please upload a CSV file'
                                        styles={{ title: { margin: '0px' } }}
                                        icon={<IconExclamationCircle size='20' stroke='1.5' />}
                                    />
                                    <Space h='md' />
                                </>
                                :
                                errorReadingFile ?
                                    <>
                                        <Alert
                                            color='red'
                                            radius='md'
                                            title={`Problem uploading file. Please try again`}
                                            styles={{ title: { margin: '0px' } }}
                                            icon={<IconExclamationCircle size='20' stroke='1.5' />}
                                        />
                                        <Space h='md' />
                                    </>
                                    :
                                    wrongFileFormat ?
                                        <>
                                            <Alert
                                                color='red'
                                                radius='md'
                                                title={`Problem uploading file`}
                                                icon={<IconExclamationCircle size='20' stroke='1.5' />}
                                            >
                                                <Text>
                                                    {fileFormatProblems.join('; ')}
                                                </Text>
                                            </Alert>
                                            <Space h='md' />
                                        </>
                                        :
                                        fileOk && filename &&
                                        <>
                                            <Alert
                                                color='teal'
                                                radius='md'
                                                title={`Uploaded ${filename}`}
                                                icon={<IconCircleCheck size='20' stroke='1.5' />}
                                                styles={{ title: { margin: '0px' } }}
                                            />
                                            <Space h='md' />
                                        </>
                        }
                        {
                            fileOk && filename ?
                                <>
                                    <>
                                        <Text size='sm' color='dimmed'>
                                            The Alias API will be called for every participant in the uploaded dataset. This may take up to an hour. The results will appear under the "Raw" tab on the dashboard.
                                        </Text>
                                        <Space h='md' />
                                    </>
                                    <>
                                        <Text className={classes.flexText}>
                                            Number of API credits required
                                            <Text component='span' className={classes.flexTextSpan}>{numRows.toLocaleString()}</Text>
                                        </Text>
                                        <Space h='xs' />
                                        <Text className={classes.flexText}>
                                            Your API credit balance
                                            <Text component='span' className={classes.flexTextSpan}>{APICreditsAvailable.toLocaleString()}</Text>
                                        </Text>
                                        <Space h='xs' />
                                        <Text className={classes.flexText}>
                                            {
                                                APICreditsAvailable - numRows < 0 ?
                                                    'Number of API credits required'
                                                    :
                                                    'Number of API credits remaining'
                                            }
                                            <Text component='span' className={classes.flexTextSpan}>
                                                {Math.abs((APICreditsAvailable - numRows)).toLocaleString()}
                                            </Text>
                                        </Text>
                                        <Space h='md' />
                                        {
                                            APICreditsAvailable - numRows < 0 ?
                                            <Tooltip label="You need to purchase at least 5 credits" disabled={numRows - APICreditsAvailable > 5}>
                                                <Button
                                                    radius='md'
                                                    size='md'
                                                    sx={{ width: '100%' }}
                                                    color='red'
                                                    variant='outline'
                                                    component={Link}
                                                    to={'/account?credits=' + calcCreditsToPurchase(numRows, APICreditsAvailable)}
                                                    >
                                                    Buy { calcCreditsToPurchase(numRows, APICreditsAvailable)
                                                    } credits
                                                </Button>
                                            </Tooltip>
                                                :
                                                <>
                                                    <Space h='md' />
                                                    <Button
                                                        radius='md'
                                                        size='md'
                                                        sx={{ width: '100%' }}
                                                        onClick={runAliasAPI}
                                                        loading={isStartingCall}
                                                        disabled={isStartingCall}
                                                    >
                                                        {isStartingCall === false && 'Run Alias API on dataset'}
                                                    </Button>
                                                </>
                                        }
                                    </>
                                </>
                                :
                                <>
                                    <Text size='sm' color='dimmed'>
                                         CSV with one row per participant and columns participant_id and survey_id.
                                        All other columns should either be questions (with column names set to the question ids) or responses (with column names formatted as {"{id}"}-response).
                                        You can optionally include a question_histories column and fingerprint_id column.
                                    </Text>
                                    <Space h='md' />
                                    <Button
                                        variant='subtle'
                                        size='sm'
                                        radius='md'
                                        leftIcon={<IconDownload size={16} stroke={1.5} />}
                                        onClick={downloadCSVExample}
                                    >
                                        Download example CSV
                                    </Button>
                                </>
                        }
                    </>
            }
        </Modal>
    )
};

export default AliasFileUpload;